import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { DAY_NAMES } from '../lib/constants';
import { Plus, Edit2, Trash2, X, CreditCard, IndianRupee } from 'lucide-react';

export default function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [form, setForm] = useState({
    name: '', price: 0, duration: 'monthly', deliveryDays: [0, 1, 2, 3, 4],
    totalDeliveries: 22, description: '', isActive: true
  });

  const fetchPlans = useCallback(async () => {
    try {
      const res = await api.getPlans();
      setPlans(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const resetForm = () => setForm({
    name: '', price: 0, duration: 'monthly', deliveryDays: [0, 1, 2, 3, 4],
    totalDeliveries: 22, description: '', isActive: true
  });

  const openNew = () => { resetForm(); setEditPlan(null); setShowModal(true); };
  const openEdit = (plan) => {
    setForm({
      name: plan.name, price: plan.price, duration: plan.duration,
      deliveryDays: plan.deliveryDays || [0, 1, 2, 3, 4],
      totalDeliveries: plan.totalDeliveries, description: plan.description || '',
      isActive: plan.isActive !== false
    });
    setEditPlan(plan);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editPlan) {
        await api.updatePlan(editPlan.id, form);
      } else {
        await api.createPlan(form);
      }
      setShowModal(false);
      fetchPlans();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    await api.deletePlan(id);
    fetchPlans();
  };

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      deliveryDays: f.deliveryDays.includes(day)
        ? f.deliveryDays.filter(d => d !== day)
        : [...f.deliveryDays, day].sort()
    }));
  };

  const getDayLabels = (days) => (days || []).map(d => DAY_NAMES[d]).join(', ');

  return (
    <div data-testid="plans-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Subscription Plans</h2>
          <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>Manage your subscription offerings</p>
        </div>
        <button data-testid="add-plan-button" onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all" style={{ background: '#C8A96E', color: '#000' }}>
          <Plus className="w-4 h-4" /> Add Plan
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: '#78716C' }}>Loading...</div>
      ) : plans.length === 0 ? (
        <div className="text-center py-12 rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', color: '#78716C' }}>
          <CreditCard className="w-12 h-12 mx-auto mb-3" style={{ color: '#2A241F' }} />
          <p>No plans created yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan, i) => (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className="rounded-xl p-6 transition-all duration-200 hover:-translate-y-1 fade-in"
              style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', animationDelay: `${i * 50}ms`, opacity: plan.isActive === false ? 0.5 : 1 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{
                    background: plan.duration === 'weekly' ? 'rgba(96,165,250,0.1)' : 'rgba(200,169,110,0.1)',
                    color: plan.duration === 'weekly' ? '#60A5FA' : '#C8A96E',
                    border: `1px solid ${plan.duration === 'weekly' ? 'rgba(96,165,250,0.2)' : 'rgba(200,169,110,0.2)'}`
                  }}>
                    {plan.duration === 'weekly' ? 'Weekly' : 'Monthly'}
                  </span>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-plan-${plan.id}`} onClick={() => openEdit(plan)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><Edit2 className="w-3.5 h-3.5" /></button>
                  <button data-testid={`delete-plan-${plan.id}`} onClick={() => handleDelete(plan.id)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>

              <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{plan.name}</h3>

              <div className="flex items-baseline gap-1 mb-3">
                <IndianRupee className="w-4 h-4" style={{ color: '#C8A96E' }} />
                <span className="text-3xl font-bold" style={{ fontFamily: 'Outfit', color: '#C8A96E' }}>{plan.price.toLocaleString('en-IN')}</span>
                <span className="text-sm" style={{ color: '#78716C' }}>/{plan.duration === 'weekly' ? 'week' : 'month'}</span>
              </div>

              <div className="space-y-2 text-sm" style={{ color: '#A8A29E' }}>
                <div className="flex items-center justify-between">
                  <span>Deliveries</span>
                  <span style={{ color: '#F5F5F4' }}>{plan.totalDeliveries}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Days</span>
                  <span style={{ color: '#F5F5F4' }}>{getDayLabels(plan.deliveryDays)}</span>
                </div>
              </div>

              {plan.description && (
                <p className="mt-3 text-xs" style={{ color: '#78716C' }}>{plan.description}</p>
              )}

              {plan.isActive === false && (
                <div className="mt-3">
                  <span className="badge-inactive px-2 py-0.5 rounded-full text-xs font-semibold">Inactive</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-lg rounded-xl max-h-[85vh] overflow-y-auto" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.3)' }}>
            <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid #2A241F' }}>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{editPlan ? 'Edit Plan' : 'Add New Plan'}</h3>
              <button data-testid="close-plan-modal" onClick={() => setShowModal(false)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Plan Name *</label>
                <input data-testid="plan-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Monthly - All Days" className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Price (Rs.) *</label>
                  <input data-testid="plan-price" type="number" required min={1} value={form.price} onChange={e => setForm({...form, price: parseInt(e.target.value) || 0})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Duration *</label>
                  <select data-testid="plan-duration" value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Total Deliveries *</label>
                <input data-testid="plan-deliveries" type="number" required min={1} value={form.totalDeliveries} onChange={e => setForm({...form, totalDeliveries: parseInt(e.target.value) || 0})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#C8A96E' }}>Delivery Days *</label>
                <div className="flex flex-wrap gap-2">
                  {DAY_NAMES.slice(0, 5).map((day, idx) => (
                    <button key={idx} type="button" onClick={() => toggleDay(idx)}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                      style={{
                        background: form.deliveryDays.includes(idx) ? 'rgba(200,169,110,0.2)' : '#100E0C',
                        border: `1px solid ${form.deliveryDays.includes(idx) ? 'rgba(200,169,110,0.5)' : '#2A241F'}`,
                        color: form.deliveryDays.includes(idx) ? '#C8A96E' : '#A8A29E'
                      }}
                    >{day}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Description</label>
                <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Brief description" className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>

              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#C8A96E' }}>Active</label>
                <button type="button" onClick={() => setForm({...form, isActive: !form.isActive})}
                  className="w-10 h-5 rounded-full transition-all relative"
                  style={{ background: form.isActive ? '#C8A96E' : '#2A241F' }}
                >
                  <div className="w-4 h-4 rounded-full absolute top-0.5 transition-all" style={{ background: '#F5F5F4', left: form.isActive ? '22px' : '2px' }} />
                </button>
              </div>

              <div className="flex gap-3 pt-2">
                <button data-testid="save-plan-button" type="submit" className="px-6 py-2.5 rounded-lg text-sm font-semibold" style={{ background: '#C8A96E', color: '#000' }}>
                  {editPlan ? 'Update Plan' : 'Create Plan'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2.5 rounded-lg text-sm font-medium" style={{ background: '#2A241F', color: '#A8A29E' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
